/*
 * Purpose: Admin card — live pricing subhead A/B test results
 * Last modified: 2026-06-29 | Agent
 * Completeness: 96
 */
import { useCallback, useEffect, useState } from 'react';
import { FlaskConical, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ContextualError } from '@/components/errors/ContextualError';
import { fetchPricingSubheadExperiment } from '@/lib/adminApi';
import { APP_ROUTES } from '@/lib/appRoutes';
import type { PricingSubheadExperimentSummary } from '@/lib/siteAnalyticsTypes';

export function PricingSubheadExperimentPanel() {
  const [experiment, setExperiment] = useState<PricingSubheadExperimentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setExperiment(await fetchPricingSubheadExperiment(14));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load experiment');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const leaderBadge =
    experiment?.leader === 'a'
      ? 'A leading'
      : experiment?.leader === 'b'
        ? 'B leading'
        : experiment?.leader === 'tie'
          ? 'Tie'
          : 'Collecting data';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-[hsl(var(--ab-green-core))]" />
              Pricing subhead A/B test
            </CardTitle>
            <CardDescription className="mt-1">
              Live on <code className="text-[10px]">/pricing</code> — conversion = Stripe table
              scrolled into view (35%+ visible).
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {experiment && (
              <Badge variant={experiment.leader === 'insufficient_data' ? 'secondary' : 'default'}>
                {leaderBadge}
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <ContextualError
            message={error}
            action={{ label: 'Retry', onClick: () => void load() }}
            secondaryAction={{ label: 'Analytics', href: APP_ROUTES.adminAnalytics }}
          />
        )}

        {experiment && (
          <>
            <p className="text-sm text-muted-foreground">{experiment.leaderDetail}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {experiment.variants.map((v) => (
                <div
                  key={v.variant}
                  className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{v.label}</p>
                    <Badge variant="outline" className="text-[10px]">
                      {v.conversionRate}% conv.
                    </Badge>
                  </div>
                  <dl className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div>
                      <dt className="text-muted-foreground">Impressions</dt>
                      <dd className="font-semibold text-base tabular-nums">{v.impressions}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Table views</dt>
                      <dd className="font-semibold text-base tabular-nums">{v.pricingTableViews}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Support</dt>
                      <dd className="font-semibold text-base tabular-nums">{v.supportClicks}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Storage: {experiment.storage} · Last {experiment.days}d · Updated{' '}
              {new Date(experiment.updatedAt).toLocaleTimeString()}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
